import { create } from 'zustand';

/**
 * Signals-tab module-scope UI state (bead .15, spec 7; archive intent added
 * QA batch 2 unit B6, D-I). Plain `create<State>((set) => ({ ... }))` per
 * CLAUDE.md "Zustand for Module-Scope Client State" — every action lives ON
 * the store (not as a module-level setter, unlike `panel-width-store.ts`).
 */
export interface SignalsUiState {
  /** Signal id the row list should auto-expand once it appears, or null. */
  pendingExpandId: string | null;
  setPendingExpandId: (id: string | null) => void;
  /**
   * One-shot "open the Signal card's archive form" signal (D-I) — a row's
   * or Timeline line's Archive click (`SignalRowActions.tsx`'s
   * `onArchiveRequest`, QA batch 2 unit B7) sets this; the Signal card's
   * `useSignalActionCard` (owned by `SignalDetail`) reads it, opens the
   * inline form, and clears it — only when the intent names one of its
   * options or its own row (qa-batch5-plan.md D-A), so an intent for a row
   * that has not mounted yet is never swallowed by another. The form's open
   * state itself lives in that hook, not here. `seq` (not just
   * `signalId`) so re-requesting the SAME signal still fires the card's
   * one-shot effect — a bare id can't distinguish "request again" from "no
   * new request," the same reason `pendingExpandId`'s consumer keys its
   * scroll effect on identity rather than a boolean.
   */
  archiveIntent: { signalId: string; seq: number } | null;
  /** Sets `archiveIntent` to `{ signalId, seq: seq + 1 }` — monotonic across repeats of the same id. */
  requestArchive: (signalId: string) => void;
  /** Consumed by the Signal card once it has acted on the current intent. */
  clearArchiveIntent: () => void;
  /**
   * Deep-linked `?signal=` ids already told "That signal isn't in Terra any
   * more" (plan 2026-10-13-001 U1) — one toast per id per page session.
   * `useSignalTableModel` adds the id synchronously BEFORE calling `toast`,
   * so a StrictMode effect double-invoke (or a remount) sees it and no-ops.
   */
  missingSignalToastIds: ReadonlySet<string>;
  markMissingSignalToasted: (signalId: string) => void;
  /**
   * `?signal=` ids whose Slack "Open in Terra" click was already attributed
   * (plan 2026-10-13-001 U2, `useSlackClickAttribution`) — one
   * `notification-opened` POST per id per page session. The hook adds the id
   * synchronously BEFORE the POST, so a StrictMode effect double-invoke (or a
   * remount while the POST is in flight) sees it and no-ops.
   */
  attributedSignalIds: ReadonlySet<string>;
  markAttributed: (signalId: string) => void;
  /**
   * The `?signal=` id the MODEL itself last wrote (row click, auto-expand-next,
   * Archive click) — i.e. an in-app selection, never a link landing. Read by
   * `useSignalTableModel`'s mount capture: a remount (tab switch and back)
   * that finds this same id in the URL is not a deep link, so it never pins,
   * toasts or attributes it (R1). Module-scope on purpose — it has to
   * outlive the model's unmount. Set synchronously in the event handler.
   */
  inAppExpandedId: string | null;
  setInAppExpandedId: (id: string | null) => void;
}

/** Exported per the CLAUDE.md zustand convention — tests reset with `setState(initialState, true)`. */
export const initialState: Pick<
  SignalsUiState,
  'pendingExpandId' | 'archiveIntent' | 'missingSignalToastIds' | 'attributedSignalIds' | 'inAppExpandedId'
> = {
  pendingExpandId: null,
  archiveIntent: null,
  missingSignalToastIds: new Set<string>(),
  attributedSignalIds: new Set<string>(),
  inAppExpandedId: null,
};

export const useSignalsUiStore = create<SignalsUiState>((set, get) => ({
  ...initialState,
  setPendingExpandId: (id) => set({ pendingExpandId: id }),
  requestArchive: (signalId) =>
    set({ archiveIntent: { signalId, seq: (get().archiveIntent?.seq ?? 0) + 1 } }),
  clearArchiveIntent: () => set({ archiveIntent: null }),
  markMissingSignalToasted: (signalId) =>
    set({ missingSignalToastIds: new Set(get().missingSignalToastIds).add(signalId) }),
  markAttributed: (signalId) =>
    set({ attributedSignalIds: new Set(get().attributedSignalIds).add(signalId) }),
  setInAppExpandedId: (id) => set({ inAppExpandedId: id }),
}));
